/**
 * Any character belonging to the Arabic script.
 *
 * A Unicode property escape rather than a hand-written code-point range: it
 * covers every Arabic block, including the presentation forms and supplements,
 * and cannot drift out of date or accidentally swallow a non-letter such as the
 * byte-order mark.
 */
const ARABIC = /\p{Script=Arabic}/u;

/**
 * True when a string carries no Arabic-script characters.
 *
 * Used to decide whether a label needs `dir="ltr"` and an English `lang`: most
 * tool names are Latin and belong left-to-right, but a few are Arabic and must
 * keep the page's own direction and voice.
 */
export function isLatinText(value: string): boolean {
  return !ARABIC.test(value);
}

/**
 * Direction and language attributes for a label that may be in either script.
 * Returns an empty object for Arabic so the element simply inherits the page.
 *
 * The second arm spells out `dir?: undefined` rather than `Record<string,
 * never>`: indexing the latter yields `never`, which collapses the union to
 * `'ltr'` and lets a caller read `.dir` on the Arabic branch with no type error
 * and an undefined at runtime.
 */
export function scriptAttributes(
  value: string,
): { dir: 'ltr'; lang: 'en' } | { dir?: undefined; lang?: undefined } {
  return isLatinText(value) ? { dir: 'ltr', lang: 'en' } : {};
}
